import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  Crown,
  Zap,
  Star,
  Users,
  FileText,
  Clock,
  X,
  Mail,
  Key,
  Sparkles,
} from "lucide-react";
import Modal from "@core/modal";
import { Button } from "@core/button";
import { Text } from "@core/text";
import { Input } from "@core/input";
import { motion, AnimatePresence } from "framer-motion";
import { saveEmailForSubscription } from "@packages/api/madlenAPI";
import { useAppDispatch, useAppSelector } from "@packages/store/hooks";
import { setPremium } from "@packages/store/userSlice";

interface ProMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CongratsAnimationProps {
  onClose: () => void;
}

const CongratsAnimation = ({ onClose }: CongratsAnimationProps) => {
  const { t } = useTranslation();

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        zIndex: 10000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <motion.div
        className="w-full h-full absolute inset-0 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="relative max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto custom-scrollbar">
          {/* Confetti effect */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ pointerEvents: "none" }}
          >
            {Array.from({ length: 30 }).map((_, i) => (
              <motion.div
                key={i}
                className="absolute"
                initial={{
                  x: Math.random() * window.innerWidth,
                  y: -20,
                  scale: Math.random() * 0.5 + 0.5,
                  opacity: 1,
                }}
                animate={{
                  y: window.innerHeight + 20,
                  rotate: Math.random() * 360,
                  opacity: 0,
                }}
                transition={{
                  duration: Math.random() * 2 + 1,
                  ease: "easeOut",
                  delay: Math.random() * 0.5,
                }}
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: Math.random() > 0.5 ? "50%" : "0",
                  background: [
                    "#FF5733",
                    "#33FF57",
                    "#3357FF",
                    "#FF33A8",
                    "#33FFF5",
                    "#F5FF33",
                  ][Math.floor(Math.random() * 6)],
                }}
              />
            ))}
          </div>

          {/* Success message */}
          <motion.div
            className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-xl border border-gray-200 dark:border-gray-700 text-center z-10 relative"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", damping: 12 }}
          >
            <div className="flex flex-col sm:flex-row items-center gap-4 sm:space-x-6">
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: "spring", damping: 8 }}
                className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center flex-shrink-0"
              >
                <Check
                  size={32}
                  className="text-green-600 dark:text-green-400"
                />
              </motion.div>

              <div className="flex-1 text-center sm:text-left">
                <Text variant="h3" className="font-bold mb-1">
                  {t("pro.congrats.title", "Congratulations!")}
                </Text>
                <Text variant="p" className="text-gray-600 dark:text-gray-300">
                  {t(
                    "pro.congrats.message",
                    "Your account has been upgraded to Premium!",
                  )}
                </Text>
              </div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full sm:w-auto"
              >
                <Button
                  onClick={onClose}
                  size="lg"
                  rounded="xl"
                  bgColor="bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700"
                  textColor="text-white"
                  className="w-full sm:w-auto"
                >
                  {t("pro.congrats.button", "Start Exploring")}
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

const ProMemberModal = ({ isOpen, onClose }: ProMemberModalProps) => {
  const { t } = useTranslation();
  const [upgradeCode, setUpgradeCode] = useState("");
  const [showCongrats, setShowCongrats] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setShowCongrats(false);
      setUpgradeCode("");
      setIsValidating(false);
      setErrorMessage(null);
    }
  }, [isOpen]);

  // Debug effect to log state changes
  useEffect(() => {
    console.log("showCongrats state:", showCongrats);
  }, [showCongrats]);

  const handleUpgrade = () => {
    if (!upgradeCode.trim()) return;

    setIsValidating(true);
    setErrorMessage(null);
    console.log("Starting validation process...");

    setIsValidating(false);

    if (upgradeCode.trim().toUpperCase() === "GELECEKMADLENDE") {
      dispatch(setPremium(new Date()));
      setShowCongrats(true);
    } else if (upgradeCode.trim().toUpperCase() === "MADLENTEDBODRUM") {
      dispatch(
        setPremium(new Date(new Date().setMonth(new Date().getMonth() + 1))),
      );
      setShowCongrats(true);
    } else {
      // Show error message for invalid code
      setErrorMessage(
        t("pro.code.invalid", "Invalid premium code. Please try again."),
      );
      console.log("Invalid premium code");
    }
  };

  const handleCongratsClose = () => {
    setShowCongrats(false);
    onClose();
    window.location.reload();
  };

  const features = [
    {
      icon: <Zap size={18} className="text-primary-500" />,
      title: t("pro.features.unlimited.title", "Unlimited AI Assistants"),
      description: t(
        "pro.features.unlimited.description",
        "Access all AI tools without limitations",
      ),
    },
    {
      icon: <FileText size={18} className="text-primary-500" />,
      title: t("pro.features.documents.title", "Essay Grading"),
      description: t(
        "pro.features.documents.description",
        "Advanced essay analysis and feedback",
      ),
    },
    {
      icon: <Users size={18} className="text-primary-500" />,
      title: t("pro.features.students.title", "Student Integration"),
      description: t(
        "pro.features.students.description",
        "Connect with your students seamlessly",
      ),
    },
    {
      icon: <Sparkles size={18} className="text-primary-500" />,
      title: t("pro.features.priority.title", "Priority Support"),
      description: t(
        "pro.features.priority.description",
        "Get dedicated support when you need it",
      ),
    },
  ];

  return (
    <>
      {/* Only show the modal if isOpen is true AND showCongrats is false */}
      {isOpen && !showCongrats && (
        <Modal
          show={true}
          onClose={onClose}
          bare={true}
          outerClassName="max-w-6xl lg:max-w-6xl max-w-full h-full lg:h-auto"
          hideCloseButton={true}
        >
          <div className="relative h-full lg:h-auto">
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white dark:bg-dark-600 hover:bg-gray-100 dark:hover:bg-dark-500 transition-colors shadow-md lg:shadow-none"
            >
              <X size={20} className="text-gray-600 dark:text-gray-300" />
            </button>

            {/* Mobile Layout */}
            <div className="lg:hidden h-full flex flex-col bg-white dark:bg-dark-500 overflow-hidden">
              {/* Mobile Header - Fixed */}
              <div className="bg-gray-50 dark:bg-dark-600 p-6 pt-16 flex-shrink-0">
                <div className="text-center mb-6">
                  <Text
                    variant="h2"
                    className="font-bold text-gray-900 dark:text-white mb-2"
                  >
                    {t("pro.form.title", "Enter Upgrade Code")}
                  </Text>
                  <Text
                    variant="p"
                    className="text-gray-600 dark:text-gray-300"
                  >
                    {t(
                      "pro.form.description",
                      "Already have a premium code? Enter it below to unlock all features.",
                    )}
                  </Text>
                </div>

                {/* Mobile Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("pro.form.label", "Premium Code")}
                    </label>
                    <Input
                      placeholder={t(
                        "pro.code.placeholder",
                        "Enter your upgrade code",
                      )}
                      value={upgradeCode}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setUpgradeCode(e.target.value);
                        setErrorMessage(null);
                      }}
                      leftIcon={<Key size={16} className="text-gray-400" />}
                      size="48px"
                      disabled={isValidating}
                      classNames={{
                        input: `bg-white dark:bg-dark-500 border-gray-200 dark:border-dark-400 focus:border-primary-500 focus:ring-primary-500 ${
                          errorMessage
                            ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                            : ""
                        }`,
                      }}
                    />
                    {errorMessage && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-500 text-sm mt-2 flex items-center gap-2"
                      >
                        <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                          <X size={12} className="text-white" />
                        </div>
                        {errorMessage}
                      </motion.div>
                    )}
                  </div>

                  <Button
                    onClick={handleUpgrade}
                    size="lg"
                    rounded="xl"
                    bgColor="bg-primary-500 hover:bg-primary-600"
                    textColor="text-white"
                    disabled={!upgradeCode.trim() || isValidating}
                    className="w-full h-12"
                  >
                    {isValidating ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {t("pro.code.validating", "Validating...")}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-2">
                        <Sparkles size={18} />
                        {t("pro.code.upgrade", "Upgrade to Premium")}
                      </div>
                    )}
                  </Button>
                </div>
              </div>

              {/* Mobile Features Section - Scrollable */}
              <div className="flex-1 overflow-y-auto custom-scrollbar p-6">
                <div className="space-y-6 mb-8">
                  {features.map((feature, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-4"
                    >
                      <div className="flex-shrink-0 w-10 h-10 bg-gray-50 dark:bg-dark-600 rounded-lg flex items-center justify-center border border-gray-200 dark:border-dark-400">
                        {feature.icon}
                      </div>
                      <div className="flex-1">
                        <Text
                          variant="h4"
                          className="font-semibold text-gray-900 dark:text-white mb-1"
                        >
                          {feature.title}
                        </Text>
                        <Text
                          variant="p"
                          className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed"
                        >
                          {feature.description}
                        </Text>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Mobile Contact Info */}
                <div className="bg-gray-50 dark:bg-dark-600 rounded-xl p-4 border border-gray-200 dark:border-dark-400 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <Mail
                        size={16}
                        className="text-blue-600 dark:text-blue-400"
                      />
                    </div>
                    <div>
                      <Text
                        variant="p"
                        className="text-gray-600 dark:text-gray-300 text-sm"
                      >
                        {t("pro.contact.title", "Need help? Contact us")}
                      </Text>
                      <Text
                        variant="p"
                        className="font-semibold text-blue-600 dark:text-blue-400 text-sm"
                      >
                        hello@madlen.io
                      </Text>
                    </div>
                  </div>
                </div>

                {/* Mobile Usage Notice */}
                <div className="p-4 bg-gray-50 dark:bg-dark-600 rounded-xl border border-gray-200 dark:border-dark-400">
                  <Text
                    variant="p"
                    className="text-gray-600 dark:text-gray-300 text-sm text-center"
                  >
                    {t(
                      "pro.limits.notice",
                      "Free accounts have limited usage. Upgrade to premium for unlimited access.",
                    )}
                  </Text>
                </div>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:grid grid-cols-2 min-h-[600px]">
              {/* Left Section - Promotional Content */}
              <div className="bg-gray-50 dark:bg-dark-600 p-8 lg:p-12 flex flex-col justify-center rounded-l-xl">
                <div className="max-w-md mx-auto lg:mx-0">
                  {/* Features List */}
                  <div className="space-y-6 mb-8">
                    {features.map((feature, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start gap-4"
                      >
                        <div className="flex-shrink-0 w-10 h-10 bg-white dark:bg-dark-500 rounded-lg flex items-center justify-center border border-gray-200 dark:border-dark-400">
                          {feature.icon}
                        </div>
                        <div className="flex-1">
                          <Text
                            variant="h4"
                            className="font-semibold text-gray-900 dark:text-white mb-1"
                          >
                            {feature.title}
                          </Text>
                          <Text
                            variant="p"
                            className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed"
                          >
                            {feature.description}
                          </Text>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Contact Info */}
                  <div className="bg-white dark:bg-dark-500 rounded-xl p-4 border border-gray-200 dark:border-dark-400">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                        <Mail
                          size={16}
                          className="text-blue-600 dark:text-blue-400"
                        />
                      </div>
                      <div>
                        <Text
                          variant="p"
                          className="text-gray-600 dark:text-gray-300 text-sm"
                        >
                          {t("pro.contact.title", "Need help? Contact us")}
                        </Text>
                        <Text
                          variant="p"
                          className="font-semibold text-blue-600 dark:text-blue-400 text-sm"
                        >
                          hello@madlen.io
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Section - Upgrade Form */}
              <div className="bg-white dark:bg-dark-500 p-8 lg:p-12 flex flex-col justify-center rounded-r-xl">
                <div className="max-w-md mx-auto lg:mx-0 w-full">
                  {/* Form Header */}
                  <div className="text-center mb-8">
                    <Text
                      variant="h3"
                      className="font-bold text-gray-900 dark:text-white mb-2"
                    >
                      {t("pro.form.title", "Enter Upgrade Code")}
                    </Text>
                    <Text
                      variant="p"
                      className="text-gray-600 dark:text-gray-300"
                    >
                      {t(
                        "pro.form.description",
                        "Already have a premium code? Enter it below to unlock all features.",
                      )}
                    </Text>
                  </div>

                  {/* Upgrade Form */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t("pro.form.label", "Premium Code")}
                      </label>
                      <Input
                        placeholder={t(
                          "pro.code.placeholder",
                          "Enter your upgrade code",
                        )}
                        value={upgradeCode}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setUpgradeCode(e.target.value);
                          setErrorMessage(null);
                        }}
                        leftIcon={<Key size={16} className="text-gray-400" />}
                        size="48px"
                        disabled={isValidating}
                        classNames={{
                          input: `bg-gray-50 dark:bg-dark-600 border-gray-200 dark:border-dark-400 focus:border-primary-500 focus:ring-primary-500 ${
                            errorMessage
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : ""
                          }`,
                        }}
                      />
                      {errorMessage && (
                        <motion.div
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-500 text-sm mt-2 flex items-center gap-2"
                        >
                          <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                            <X size={12} className="text-white" />
                          </div>
                          {errorMessage}
                        </motion.div>
                      )}
                    </div>

                    <Button
                      onClick={handleUpgrade}
                      size="lg"
                      rounded="xl"
                      bgColor="bg-primary-500 hover:bg-primary-600"
                      textColor="text-white"
                      disabled={!upgradeCode.trim() || isValidating}
                      className="w-full h-12"
                    >
                      {isValidating ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          {t("pro.code.validating", "Validating...")}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <Sparkles size={18} />
                          {t("pro.code.upgrade", "Upgrade to Premium")}
                        </div>
                      )}
                    </Button>
                  </div>

                  {/* Usage Notice */}
                  <div className="mt-8 p-4 bg-gray-50 dark:bg-dark-600 rounded-xl border border-gray-200 dark:border-dark-400">
                    <Text
                      variant="p"
                      className="text-gray-600 dark:text-gray-300 text-sm text-center"
                    >
                      {t(
                        "pro.limits.notice",
                        "Free accounts have limited usage. Upgrade to premium for unlimited access.",
                      )}
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Congratulations Animation */}
      <AnimatePresence>
        {showCongrats && <CongratsAnimation onClose={handleCongratsClose} />}
      </AnimatePresence>
    </>
  );
};

export default ProMemberModal;
